import { trackEvent } from "./analytics";

interface DismissalRecord {
	hash: string;
	dismissedAt: number;
}

const dismissDurationMs = 7 * 24 * 60 * 60 * 1000;
const entranceDelayMs = 600;
const transitionFallbackMs = 700;

function hashContent(text: string): string {
	// FNV-1a (32 bit)
	let hash = 0x811c9dc5;
	for (let i = 0; i < text.length; i++) {
		hash ^= text.charCodeAt(i);
		hash = Math.imul(hash, 0x01000193);
	}
	return (hash >>> 0).toString(16);
}

function readDismissalRecord(storageKey: string): DismissalRecord | null {
	try {
		const raw = localStorage.getItem(storageKey);
		if (raw == null) {
			return null;
		}
		const parsed = JSON.parse(raw);
		if (typeof parsed?.hash === "string" && typeof parsed?.dismissedAt === "number") {
			return parsed as DismissalRecord;
		}
	} catch {}
	return null;
}

function writeDismissalRecord(storageKey: string, record: DismissalRecord) {
	try {
		localStorage.setItem(storageKey, JSON.stringify(record));
	} catch {}
}

function whenDocumentReady(callback: () => void) {
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", callback, { once: true });
	} else {
		callback();
	}
}

function initInfoPopup(popup: HTMLElement) {
	const maybeCloseButton = popup.querySelector<HTMLButtonElement>(".info-popup__close");
	const backdrop = popup.querySelector<HTMLElement>(".info-popup__backdrop");
	const content = popup.querySelector<HTMLElement>(".info-popup__content");
	if (maybeCloseButton == null || content == null) {
		return;
	}
	const closeButton = maybeCloseButton;

	const pageId = popup.dataset.pageId ?? "unknown";
	const storageKey = `info-popup:${pageId}`;
	const contentHash = hashContent(content.innerHTML);

	const record = readDismissalRecord(storageKey);
	if (record != null && record.hash === contentHash && Date.now() - record.dismissedAt < dismissDurationMs) {
		return;
	}

	let previouslyFocusedElement: Element | null = null;
	let isDismissed = false;

	function onKeydown(event: KeyboardEvent) {
		if (event.key === "Escape") {
			dismiss();
		}
	}

	function show() {
		popup.hidden = false;
		// force a reflow so the transition starts from the hidden state
		void popup.offsetHeight;
		requestAnimationFrame(() => {
			popup.classList.add("is-active");
		});

		document.documentElement.classList.add("is-clipped");
		previouslyFocusedElement = document.activeElement;
		closeButton.focus();
		document.addEventListener("keydown", onKeydown);
		trackEvent("infopopup_shown");
	}

	function dismiss() {
		if (isDismissed) {
			return;
		}
		isDismissed = true;

		writeDismissalRecord(storageKey, { hash: contentHash, dismissedAt: Date.now() });
		trackEvent("infopopup_dismissed");

		document.removeEventListener("keydown", onKeydown);
		document.documentElement.classList.remove("is-clipped");

		let isHidden = false;
		function hide() {
			if (isHidden) {
				return;
			}
			isHidden = true;
			popup.hidden = true;
		}
		popup.addEventListener("transitionend", hide, { once: true });
		setTimeout(hide, transitionFallbackMs);
		popup.classList.remove("is-active");

		if (previouslyFocusedElement instanceof HTMLElement) {
			previouslyFocusedElement.focus();
		}
	}

	closeButton.addEventListener("click", dismiss);
	backdrop?.addEventListener("click", dismiss);

	whenDocumentReady(() => {
		setTimeout(show, entranceDelayMs);
	});
}

const popupElement = document.querySelector<HTMLElement>(".info-popup");
if (popupElement != null) {
	initInfoPopup(popupElement);
}
